import {authApi} from '@/api/auth.api.js'
import {bearerToken, csrfToken, isAuthenticated, reAuthenticationHandler} from '@/services/auth-session.js'
import {useTeams} from '@/composables/use-teams.js'
import {useAuth0} from '@/composables/use-auth0.js'
import {useEnv} from '@/composables/use-env.js'
import {useRailsUrls} from '@/composables/use-rails-urls.js'
import {useLoginUrl} from '@/composables/use-login-url.js'
import {joinPaths, openUrl} from '@/helpers/url.js'
import {ref} from 'vue'

//Global refs
const authPromise = ref(null)
const isRedirecting = ref(false)

/**
 * Authentication composable
 */
export function useAuthentication() {

  //Composables
  const {hasInitializedTeams, isInTeams, getTeamsAuthToken, userEmail} = useTeams()
  const {getAuth0Token, loginWithAuth0} = useAuth0()
  const {messengerLoginUrl} = useLoginUrl()

  //Process auth data
  const processAuthData = json => {

    //Store CSRF token and mark as authenticated
    bearerToken.value = json.token
    isAuthenticated.value = true
  }

  //Authenticate through Teams
  const authenticateWithTeams = async () => {

    //Must have initialized Teams before calling this
    if (!hasInitializedTeams.value) {
      throw new Error('Please call `initializeTeams()` first before trying to authenticate with Teams')
    }

    //Get token and authenticate
    const token = await getTeamsAuthToken()
    const json = await authApi.authenticateWithTeamsSSOToken(token, {
      userEmail: userEmail.value,
    })

    //Process auth data
    processAuthData(json)
  }

  //Authenticate with redirect
  const authenticateWithRedirect = async () => {

    //Already redirecting
    if (isRedirecting.value) {
      return
    }

    //Set flag
    isRedirecting.value = true

    //If messenger login URL is present, redirect there
    if (messengerLoginUrl.value) {
      console.warn('Redirecting to custom login page:', messengerLoginUrl.value)
      openUrl(messengerLoginUrl.value)
      return
    }

    //Determine login and redirect URLs
    const {railsAppRootUrl} = useRailsUrls()
    const {href} = useEnv()
    const loginUrl = joinPaths(railsAppRootUrl.value, 'login')
    const redirectUrl = encodeURIComponent(href.value)
    console.warn('Redirecting to login page with redirect URL:', redirectUrl)

    //Redirect
    openUrl(loginUrl, {redirect_url: redirectUrl})
  }

  //Authenticate
  const authenticate = async () => {

    //Already authenticating
    if (authPromise.value) {
      return authPromise.value
    }

    //Auth runner
    const run = async () => {
      try {
        if (isInTeams.value) {
          await authenticateWithTeams()
        }
        else if (messengerLoginUrl.value) {
          await authenticateWithRedirect()
        }
        else {
          await loginWithAuth0()
        }
      }
      finally {
        authPromise.value = null
        isRedirecting.value = false
      }
    }

    //Store promise and return it
    authPromise.value = run()
    return authPromise.value
  }

  //Set authenticated
  const setAuthenticated = value => isAuthenticated.value = value

  //Obtain CSRF token from the dedicated API endpoint (web mount calls this after session is ready)
  const obtainCsrfToken = async () => {
    csrfToken.value = await authApi.getCsrfToken()
  }

  //Obtain Auth0 ID token and persist it to the Rails session cookie
  const syncAuth0Cookie = async () => {
    const token = await getAuth0Token()
    if (token) {
      await authApi.syncAuthCookie(token)
    }
    else {
      throw new Error('Auth0 ID token unavailable')
    }
  }

  //Register re-authentication handler
  const registerReAuthenticationHandler = () => {
    reAuthenticationHandler.value = authenticate
  }

  //Expose interface
  return {
    authenticate,
    authenticateWithTeams,
    setAuthenticated,
    obtainCsrfToken,
    syncAuth0Cookie,
    registerReAuthenticationHandler,

    //Re-export session refs
    isAuthenticated,
    bearerToken,
    csrfToken,
  }
}
